import * as React from 'react';
import { LoaderIcon } from '@mentimeter/ragnar-visuals';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Button } from '@mentimeter/ragnar-ui/button';
import { ClientErrorCodes, ErrorCodes } from '@mentimeter/quiz-types';
import { sendMetric as sendDdMetric } from '@mentimeter/errors/sendMetric';
import type { ReactNode } from 'react';
import { Link } from '@mentimeter/ragnar-ui/link';
import { useTranslate } from '../../../../features/localization/translate';
import { StringParser } from '../../StringParserWrapper';
import { AnnouncedHeading } from '../ui/AnnouncedHeading';

const NO_AVAILABLE_QUIZ_MSG = 'quiz.controller.no_available_quiz';
const PLAYER_FAILED_MSG = 'quiz.controller.player_failed';
const WRONG_VOTE_KEY_MSG = 'quiz.controller.wrong_vote_key';
const VOTE_FAILED_MSG = WRONG_VOTE_KEY_MSG;
const VOTED_TOO_SLOW_MSG = 'quiz.controller.vote_too_slow';
const VOTED_TOO_FAST_MSG = 'quiz.controller.vote_too_fast';
const ALREADY_VOTED_MSG = 'messages.already_responded';
const RESPONSE_MSG = 'messages.response';
const NO_CONNECTION = 'quiz.controller.no_results';
const TOO_MANY_PLAYERS_MSG = 'quiz.controller.too_many_players';
const UNKNOWN_ERROR_MSG = 'quiz.controller.unknown_error';

const getErrorMessageForCode: (
  code: ErrorCodes | ClientErrorCodes | undefined,
  translate: ReturnType<typeof useTranslate>,
) => string = (code, translate) => {
  switch (code) {
    case ErrorCodes.NO_AVAILABLE_QUIZ:
      return translate(NO_AVAILABLE_QUIZ_MSG);
    case ErrorCodes.FAILED_TO_CREATE_OR_UPDATE_PLAYER:
      return translate(PLAYER_FAILED_MSG);
    case ErrorCodes.FAILED_TO_POST_VOTE:
      return translate(VOTE_FAILED_MSG);
    case ErrorCodes.PLAYER_VOTED_TOO_SLOW:
      return translate(VOTED_TOO_SLOW_MSG);
    case ErrorCodes.PLAYER_VOTED_TOO_FAST:
      return translate(VOTED_TOO_FAST_MSG);
    case ErrorCodes.PLAYER_HAS_ALREADY_VOTED:
      return translate(ALREADY_VOTED_MSG);
    case ErrorCodes.FAILED_TO_GET_RESULT:
      return translate(RESPONSE_MSG);
    case ClientErrorCodes.TOO_MANY_PLAYERS:
      return translate(TOO_MANY_PLAYERS_MSG);
    case ClientErrorCodes.NO_CONNECTION:
      return translate(NO_CONNECTION);
    default:
      return translate(UNKNOWN_ERROR_MSG);
  }
};

const EmailLink = (chunks: ReactNode) => {
  return <Link href="mailto:hello@mentimeter.com">{chunks}</Link>;
};
export const Error = ({
  code,
  preLoading,
}: {
  code?: ErrorCodes | ClientErrorCodes;
  preLoading?: boolean;
}) => {
  const translate = useTranslate();

  const onReload = React.useCallback(() => {
    sendDdMetric({ name: 'mmjs.error_reloaded_quiz', value: 1 });
    window.location.reload();
  }, []);

  const onJoinAnother = React.useCallback(() => {
    sendDdMetric({ name: 'mmjs.error_clicked_join_another_quiz', value: 1 });
    window.location.assign('/');
  }, []);

  const [showError, setShowError] = React.useState(false);
  React.useEffect(() => {
    if (!preLoading) return;
    const timer = setTimeout(() => {
      setShowError(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [preLoading]);

  if (preLoading && !showError) {
    return <LoaderIcon />;
  }

  const error = getErrorMessageForCode(code, translate);

  return (
    <>
      {error ? (
        <AnnouncedHeading
          textAlign="center"
          fontSize="112.5"
          color="text"
          fontWeight="regular"
        >
          <StringParser source={error} disableLineBreaks={false} />
        </AnnouncedHeading>
      ) : (
        <Text fontFamily="monospace" textAlign="center" fontSize="62.5">
          {/* eslint-disable-next-line menti-react-jsx/no-possibly-undefined-in-jsx */}
          Error code: {code}
        </Text>
      )}
      <Text textAlign="center" fontSize="87.5" color="text" mb="space3">
        {translate.rich('messages.general_support_message', {
          EmailLink,
        })}
      </Text>
      <Box my="space6" alignItems="center">
        <Button size="large" variant="primary" onClick={onReload}>
          {translate('buttons.reload')}
        </Button>
        <Button variant="secondary" onClick={onJoinAnother} mt="space1">
          {translate('buttons.join_quiz')}
        </Button>
      </Box>
    </>
  );
};
